import React from "react";
import API from "api";
import LocalHospitalOutlinedIcon from "@mui/icons-material/HealthAndSafety";
import Session from "helpers/session";
import {
  admittedColumn,
  memberColumn,
  lastMeasurmentColumn,
  breachColumn,
  heartRateColumn,
  weightColumn,
  temperatureColumn,
  spirometryColumn,
  pulseOxygenColumn,
  bloodGlucoseColumn,
  bloodPressureColumn,
  //column for activity
  activityColumn,
  //column for sleep
  sleepColumn,
  reviewedColumn
} from "./Main.components";
import moment from "moment";
import { memberObject } from "context/initialState/Member";
import { uMembers } from "helpers/translationStringConstants";
import { COORDINATOR } from "helpers/appConstants";
import { MEMBER_DISPATCHES } from "context/reducers/memberReducer";
import { GridCellParams } from "@mui/x-data-grid";

export const loadMembersContext = async (memberDisp: any) => {
  const { items: allMembers } = await API.kore.members.getAll(
    Session.crackCookieToken(COORDINATOR),
    "scope=coordinator&"
  );
  memberDisp({ type: MEMBER_DISPATCHES.updateList, payload: allMembers });
  return allMembers;
};

export const loadConfigurationsContext = async (
  confDisp: any,
  organization: string
) => {
  const allConfigurations =
    await API.kore.configurations.getConfigurations(organization);
  const org = allConfigurations.organizationOptions.filter((data: any) => {
    return data.organization === organization;
  })[0];
  await confDisp({ type: "OPTIONS_UPLOAD", payload: allConfigurations });
  return org?.coordinators.filter((coord: any) => {
    return coord.id === Session.crackCookieUserId(COORDINATOR);
  })[0];
};

export const loadNotificationsContext = async (notifDisp: any) => {
  const { items: allNotifications } = await API.kore.notifications.getList(
    Session.crackCookieToken(COORDINATOR),
    "acknowledged:false"
  );
  notifDisp({ type: "UPDATE_COUNT", payload: allNotifications.length });
  notifDisp({ type: "UPDATE_NOTIFICATIONS", payload: allNotifications });
};

export const formatRows = async (
  memberList: memberObject[],
  memberDisp: any,
  memberRecents: any
) => {
  // `recents` starts as `null` and is only ever populated from a *successful*
  // member load (failed loads throw upstream and never reach here). So `null`
  // means "readings cache not built yet" -> do the full fetch below. Any loaded
  // array is authoritative and reused as-is, including an empty one for a
  // coordinator/provider with no members assigned -- that is a valid terminal
  // state, not a signal to refetch.
  if (Array.isArray(memberRecents)) {
    return memberRecents.map((row: any) => {
      const updatedMember = memberList.find((m: memberObject) => m.id === row.id);
      if (!updatedMember) return row;
      return {
        ...row,
        member: updatedMember,
        reviewed: updatedMember.acknowledged?.isAcknowledged
          ? (updatedMember.acknowledged.lastAcknowledged
            ? new Date(updatedMember.acknowledged.lastAcknowledged).getTime()
            : 0)
          : null
      };
    });
  }
  const mappedPromises = memberList.map(async (member: memberObject) => {
    const { id, latestReading } = member;
    const { bp, spo2, hrt, bgl, spir, wt, temp, sleep, activity } =
      await API.kore.readings.getRecentReadings(
        id,
        Session.crackCookieToken(COORDINATOR)
      );
    const recents = {
      bp: bp || "",
      spo2: spo2 || "",
      hrt: hrt || "",
      bgl: bgl || "",
      spir: spir || "",
      wt: wt || "",
      temp: temp || "",
      sleep: sleep || "",
      activity: activity || ""
    };
    return {
      id,
      member,
      reviewed: member.acknowledged?.isAcknowledged
        ? (member.acknowledged.lastAcknowledged
          ? new Date(member.acknowledged.lastAcknowledged).getTime()
          : 0)
        : null,
      lastMeasurement: {
        readingsArray: latestReading ? [latestReading] : []
      },
      recents
    };
  });

  const rowPromises = await Promise.all(mappedPromises);

  memberDisp({
    type: MEMBER_DISPATCHES.updateRecents,
    payload: rowPromises
  });
  return rowPromises;
};

const COLUMN_OPTIONS: Record<string, string[]> = {
  bp: ["systolic", "diastolic"],
  spo2: ["spo2"],
  spir: ["FEV1", "FEV1_FVC", "FVC", "PEF"],
  hrt: ["pulse"],
  temp: ["temp"],
  bgl: ["glucose"],
  wt: ["wt"],
  activity: ["steps"],
  sleep: ["sleep"],
  riskAlerts: ["riskAlerts"],
  lastMeasurement: ["lastMeasurement"]
};

const isGroupEnabled = (
  defaultSettings: Record<string, Record<string, boolean>> | undefined,
  groupKey: string
) => {
  if (!defaultSettings) return true;

  const subFields = COLUMN_OPTIONS[groupKey];
  if (!subFields) return true;

  return subFields.some(
    field => defaultSettings[groupKey]?.[field]
  );
};

export const createColumns = (
  translation: Function,
  goToReadings: Function,
  goToMemberPage: Function,
  defaultSettings: any
) => [
  {
    field: "admitted",
    headerClassName: "main-member-list-header",
    headerName: "",
    renderHeader: () => <LocalHospitalOutlinedIcon />,
    width: 10,
    flex: 10,
    sortable: false,
    filterable: false,
    renderCell: (item: { row: { member: any } }) => {
      return admittedColumn(item.row.member);
    }
  },
  {
    field: "member",
    headerClassName: "main-member-list-header",
    headerName: translation(uMembers),
    width: 200,
    flex: 500,

    renderCell: (params: any) => {
      return memberColumn(params.row?.member, goToMemberPage, params);
    },

    valueGetter: (_value: any, row: any) => {
      const member = row?.member;
      if (!member) return "";

      return [
        member.lastName,
        member.firstName,
        member.email,
        member.site,
        member.program,
        member.groups?.[0],
        member.externId,
        member.hubIds?.length ? member.hubIds.toString() : ""
      ]
        .filter(Boolean)
        .join(" ");
    }
  },
  isGroupEnabled(defaultSettings, "riskAlerts") && {
    field: "riskAlerts",
    headerClassName: "main-member-list-header",
    headerName: "Risk Alerts",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    type: 'boolean',
    valueGetter: (_value: any, row: any) =>
      row?.member?.latestReading?.breached ?? false,
    renderCell: (item: { row: { member: { latestReading: any } } }) => {
      return breachColumn(item.row.member.latestReading);
    }
  },
  isGroupEnabled(defaultSettings, "bp") && {
    field: "bloodPressure",
    headerClassName: "main-member-list-header",
    headerName: "Blood Pressure",
    width: 100,
    flex: 300,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.bp?.measuredAt
        ? new Date(row.recents.bp.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return bloodPressureColumn(
        item.row.recents?.bp,
        item.row.member,
        goToReadings
      );
    }
  },
  isGroupEnabled(defaultSettings, "spo2") && {
    field: "pulseOxygen",
    headerClassName: "main-member-list-header",
    headerName: "Pulse Oxygen",
    width: 100,
    flex: 280,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.spo2?.measuredAt
        ? new Date(row.recents.spo2.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return pulseOxygenColumn(
        item.row.recents?.spo2,
        item.row.member,
        goToReadings
      );
    }
  },
  isGroupEnabled(defaultSettings, "spir") && {
    field: "spirometry",
    headerClassName: "main-member-list-header",
    headerName: "Spirometry",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.spir?.measuredAt
        ? new Date(row.recents.spir.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return spirometryColumn(
        item.row.recents?.spir,
        item.row.member,
        goToReadings
      );
    }
  },
  isGroupEnabled(defaultSettings, "hrt") && {
    field: "heartRate",
    headerClassName: "main-member-list-header",
    headerName: "Heart Rate",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.hrt?.measuredAt
        ? new Date(row.recents.hrt.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return heartRateColumn(item.row.recents, item.row.member, goToReadings);
    }
  },
  isGroupEnabled(defaultSettings, "temp") && {
    field: "temperature",
    headerClassName: "main-member-list-header",
    headerName: "Temperature",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.temp?.measuredAt
        ? new Date(row.recents.temp.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return temperatureColumn(
        item.row.recents?.temp,
        item.row.member,
        goToReadings
      );
    }
  },
  isGroupEnabled(defaultSettings, "bgl") && {
    field: "bloodGlucose",
    headerClassName: "main-member-list-header",
    headerName: "Blood Glucose",
    width: 100,
    flex: 280,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.bgl?.measuredAt
        ? new Date(row.recents.bgl.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return bloodGlucoseColumn(
        item.row.recents?.bgl,
        item.row.member,
        goToReadings
      );
    }
  },
  isGroupEnabled(defaultSettings, "wt") && {
    field: "weight",
    headerClassName: "main-member-list-header",
    headerName: "Weight",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.wt?.measuredAt
        ? new Date(row.recents.wt.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return weightColumn(item.row.recents?.wt, item.row.member, goToReadings);
    }
  },
  isGroupEnabled(defaultSettings, "activity") && {
    field: "activity",
    headerClassName: "main-member-list-header",
    headerName: "Activity",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.activity?.measuredAt
        ? new Date(row.recents.activity.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return activityColumn(item.row.recents?.activity, item.row.member, goToReadings);
    }
  },
  isGroupEnabled(defaultSettings, "sleep") && {
    field: "sleep",
    headerClassName: "main-member-list-header",
    headerName: "Sleep",
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    valueGetter: (_value: any, row: any) =>
      row?.recents?.sleep?.measuredAt
        ? new Date(row.recents.sleep.measuredAt).getTime()
        : 0,
    renderCell: (item: any) => {
      return sleepColumn(item.row.recents?.sleep, item.row.member, goToReadings);
    }
  },
  isGroupEnabled(defaultSettings, "lastMeasurement") && {
    field: "lastMeasurement",
    headerClassName: "main-member-list-header",
    headerName: "Latest Reading",
    width: 260,
    flex: 500,
    sortable: true,
    filterable: false,

    renderCell: (params: any) => {
      return lastMeasurmentColumn(params.row?.lastMeasurement);
    },
    valueGetter: (_value: any, row: any) =>
      row?.lastMeasurement?.readingsArray?.[0]
        ? moment(row.lastMeasurement.readingsArray[0].measuredAt)
        : moment("01-01-1900")
  },
  {
    field: "reviewed",
    headerClassName: "main-member-list-header",
    headerName: "Reviewed",
    align: "center",
    minWidth: 100,
    width: 100,
    flex: 250,
    sortable: true,
    filterable: false,
    renderCell: (item: any) => {
      return (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {reviewedColumn(item.row.member?.acknowledged)}
        </div>
      );
    },
    valueGetter: (_value: any, row: any) => {
      const a = row?.member?.acknowledged;
      const isReviewed = a?.isAcknowledged ?? false;
      const timestamp = a?.lastAcknowledged
        ? new Date(a.lastAcknowledged).getTime()
        : 0;
      return { isReviewed, timestamp };
    },
    sortComparator: (v1: any, v2: any) => {
      if (v1.isReviewed !== v2.isReviewed) {
        return v1.isReviewed ? 1 : -1;
      }
      return v1.timestamp - v2.timestamp;
    }
  }
];

export const clientSettings = (clientId: string) => ({
  entity: "Client",
  id: clientId,
  system: {
    settings: {
      columnVisibility: {
        member: true,
        riskAlerts: false,
        bloodPressure: true,
        pulseOxygen: true,
        spirometry: true,
        heartRate: true,
        temperature: true,
        bloodGlucose: true,
        weight: true,
        activity: true,
        sleep: true,
        lastMeasurement: false
      },
      columnOrder: [
        "admitted",
        "member",
        "riskAlerts",
        "bloodPressure",
        "pulseOxygen",
        "spirometry",
        "heartRate",
        "temperature",
        "bloodGlucose",
        "weight",
        "activity",
        "sleep",
        "lastMeasurement"
      ],
      density: "standard"
    }
  },
  organization: "l365"
});

export const defaultSettings = () => ({
  entity: "Default",
  system: {
    settings: {
      spir: {
        FEV1: true,
        FEV1_FVC: true,
        FVC: true,
        PEF: true
      },
      bp: {
        diastolic: true,
        systolic: true
      },
      bgl: {
        glucose: true
      },
      hrt: {
        pulse: true
      },
      spo2: {
        spo2: true
      },
      activity: {
        steps: true
      },
      sleep: {
        sleep: true
      },
      temp: {
        temp: true
      },
      wt: {
        wt: true
      },
      riskAlerts: {
        riskAlerts: true
      },
      lastMeasurement: {
        lastMeasurement: true
      }
    }
  },
  organization: "l365"
});
